import { Box, Button, Container, Flex, FormControl, FormLabel } from '@chakra-ui/react';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useFlashQueryParam } from '../../../hooks/use-flash-query-param';
import { useQuery } from '../../../hooks/use-query';
import { useResetQueryParams } from '../../../hooks/use-reset-query-params';
import { useSearch } from '../../../hooks/use-search';
import { IEnergySavingsApplication } from '../../../models/energy-savings-application';
import { useMst } from '../../../setup/root';
import {
  EPermitApplicationStatusGroup,
  EPermitApplicationSubmitterSortFields,
  EPermitClassificationCode,
} from '../../../types/enums';
import { getRuntimeBooleanMetaValue } from '../../../utils/utility-functions';
import { BlueTitleBar } from '../../shared/base/blue-title-bar';
import { Paginator } from '../../shared/base/inputs/paginator';
import { PerPageSelect } from '../../shared/base/inputs/per-page-select';
import { ModelSearchInput } from '../../shared/base/model-search-input';
import { SharedSpinner } from '../../shared/base/shared-spinner';
import { EnergySavingsApplicationCard } from '../../shared/energy-savings-applications/energy-savings-application-card';
import { EnergySavingsApplicationFilter } from '../../shared/energy-savings-applications/energy-savings-application-filter';
import { RouterLinkButton } from '../../shared/navigation/router-link-button';
import { SortSelect } from '../../shared/select/selectors/sort-select';

interface IEnergySavingsApplicationIndexScreenProps {
  skipDefaultFilters?: boolean;
  hideBlueSection?: boolean;
  isContractorDashboard?: boolean;
}

export const EnergySavingsApplicationIndexScreen = observer(
  ({
    skipDefaultFilters = false,
    hideBlueSection = false,
    isContractorDashboard = false,
  }: IEnergySavingsApplicationIndexScreenProps) => {
    const SUBMIT_INVOICE_ENABLED = getRuntimeBooleanMetaValue('submit-invoice-enabled', true);

    const { t } = useTranslation();
    const { permitApplicationStore, sandboxStore, userStore } = useMst();
    const {
      tablePermitApplications,
      currentPage,
      totalPages,
      totalCount,
      countPerPage,
      handleCountPerPageChange,
      handlePageChange,
      isSearching,
      statusFilterToGroup,
      hasResetableFilters,
    } = permitApplicationStore;

    const { currentSandboxId } = sandboxStore;
    const location = useLocation();
    const query = useQuery();
    const currentUser = userStore.currentUser;
    const isContractorSuspended = currentUser?.contractorSuspended || false;
    const isContractorDeactivated = currentUser?.contractorAccessBlocked || false;

    const {
      setUserGroupFilter,
      setAudienceTypeFilter,
      setSubmissionTypeFilter,
      setIsSupportedApplicationsPageFilter,
      search,
    } = permitApplicationStore;
    const requirementTemplateId = query.get('requirementTemplateId');
    const templateVersionId = query.get('templateVersionId');
    const filters = t('energySavingsApplication.filter', { returnObjects: true }) as { [key: string]: string };

    // Only set default filters if skipDefaultFilters is false
    React.useEffect(() => {
      if (!skipDefaultFilters) {
        const isSupportedApplicationsPage = location.pathname.endsWith('/supported-applications');

        if (isSupportedApplicationsPage) {
          // For supported applications, use backend boolean query filter
          setUserGroupFilter(EPermitClassificationCode.participant);
          setAudienceTypeFilter([EPermitClassificationCode.external, EPermitClassificationCode.internal]);
          // Show parent applications (not support requests themselves)
          setSubmissionTypeFilter([
            EPermitClassificationCode.application,
            EPermitClassificationCode.onboarding,
            EPermitClassificationCode.invoice,
          ]);
          setIsSupportedApplicationsPageFilter(true);
        } else {
          setUserGroupFilter(EPermitClassificationCode.participant);
          setAudienceTypeFilter(
            currentUser.isParticipant ? EPermitClassificationCode.external : EPermitClassificationCode.internal,
          );
          setSubmissionTypeFilter(
            currentUser.isParticipant
              ? EPermitClassificationCode.application
              : [
                  EPermitClassificationCode.application,
                  EPermitClassificationCode.onboarding,
                  EPermitClassificationCode.invoice,
                  EPermitClassificationCode.supportRequest,
                ],
          );
          setIsSupportedApplicationsPageFilter(null);
        }
      }
    }, [
      skipDefaultFilters,
      setUserGroupFilter,
      setAudienceTypeFilter,
      setSubmissionTypeFilter,
      setIsSupportedApplicationsPageFilter,
      currentUser.isParticipant,
      location.pathname,
    ]);

    useSearch(permitApplicationStore, [
      requirementTemplateId || '',
      templateVersionId || '',
      JSON.stringify(currentSandboxId) || 'null',
    ]);
    useFlashQueryParam();
    const resetQueryParams = useResetQueryParams();

    const displayedApplications = tablePermitApplications;

    const isSubmitInvoiceDisabled = !SUBMIT_INVOICE_ENABLED || isContractorSuspended || isContractorDeactivated;

    return (
      <Flex as="main" direction="column" w="full" bg="greys.white" pb="24">
        {/* <PermitApplicationStatusTabs /> */}
        {!hideBlueSection && (
          <BlueTitleBar
            title={
              location.pathname.endsWith('/supported-applications')
                ? t('site.breadcrumb.supportedApplications')
                : t('site.myApplications')
            }
          />
        )}
        <Container maxW="container.lg" pb={4}>
          <Flex as="section" direction="column" p={6} gap={6} flex={1}>
            <Flex
              gap={6}
              align={{ base: 'flex-start', md: 'flex-end' }}
              justify="space-between"
              direction={{ base: 'column', md: 'row' }}
            >
              {isContractorDashboard ? (
                <RouterLinkButton
                  to={'/new-invoice'}
                  variant="primary"
                  w={{ base: 'full', md: 'fit-content' }}
                  aria-label={
                    isSubmitInvoiceDisabled
                      ? t('contractor.suspended.buttonDisabled')
                      : `${t('landing.contractor.dashboard.submitInvoice')} - ${t('energySavingsApplication.newInvoiceSubmission')}`
                  }
                  isDisabled={isSubmitInvoiceDisabled}
                >
                  {t('landing.contractor.dashboard.submitInvoice')}
                </RouterLinkButton>
              ) : (
                <RouterLinkButton
                  to={'/new-application'}
                  variant="primary"
                  w={{ base: 'full', md: 'fit-content' }}
                  aria-label={t('energySavingsApplication.start')}
                >
                  {t('energySavingsApplication.start')}
                </RouterLinkButton>
              )}
              <Flex
                align={{ md: 'end' }}
                gap={4}
                direction={{ base: 'column', md: 'row' }}
                w={{ base: 'full', md: 'fit-content' }}
              >
                <Flex direction={{ base: 'row' }} alignItems={{ md: 'end', base: 'end' }} gap={4}>
                  <EnergySavingsApplicationFilter
                    statusGroups={[
                      EPermitApplicationStatusGroup.draft,
                      EPermitApplicationStatusGroup.submitted,
                      EPermitApplicationStatusGroup.revisionsRequested,
                      EPermitApplicationStatusGroup.inReview,
                      EPermitApplicationStatusGroup.approved,
                      EPermitApplicationStatusGroup.approvedPending,
                      EPermitApplicationStatusGroup.approvedPaid,
                      EPermitApplicationStatusGroup.ineligible,
                      EPermitApplicationStatusGroup.resubmitted,
                    ]}
                  />
                  {hasResetableFilters && (
                    <Button variant="link" mb={2} onClick={resetQueryParams}>
                      {t('ui.resetFilters')}
                    </Button>
                  )}
                  <FormControl flex={1}>
                    <FormLabel>{t('ui.search')}</FormLabel>
                    <Box minW={{ md: 250, base: '100%' }}>
                      <ModelSearchInput
                        searchModel={permitApplicationStore}
                        inputGroupProps={{ w: { md: '100%', base: 'full' } }}
                        debounceTimeInMilliseconds={300}
                      />
                    </Box>
                  </FormControl>
                </Flex>
                <SortSelect
                  searchModel={permitApplicationStore}
                  i18nPrefix="permitApplication"
                  sortFields={Object.values(EPermitApplicationSubmitterSortFields)}
                />
              </Flex>
            </Flex>

            {isSearching ? (
              <Flex py="50" w="full">
                <SharedSpinner h={50} w={50} />
              </Flex>
            ) : displayedApplications.length === 0 ? (
              <Flex direction="column" w="full">
                {t('landing.contractor.dashboard.uploadInfo') && (
                  <Box borderBottom="2px solid" borderColor="greys.lightGrey" mt={4} />
                )}
                <Flex
                  py="10"
                  w="full"
                  justify={t('landing.contractor.dashboard.uploadInfo') ? 'flex-start' : 'center'}
                  role="status"
                  aria-live="polite"
                >
                  {t('landing.contractor.dashboard.uploadInfo') || t('errors.noResults')}
                </Flex>
              </Flex>
            ) : (
              displayedApplications.map((pa) => (
                <EnergySavingsApplicationCard key={pa.id} energySavingsApplication={pa as IEnergySavingsApplication} />
              ))
            )}
          </Flex>
          <Flex px={6} justify="space-between">
            <PerPageSelect
              handleCountPerPageChange={handleCountPerPageChange}
              countPerPage={countPerPage}
              totalCount={totalCount}
            />
            <Paginator
              current={currentPage}
              total={totalCount}
              totalPages={totalPages}
              pageSize={countPerPage}
              handlePageChange={handlePageChange}
              showLessItems={true}
            />
          </Flex>
        </Container>
      </Flex>
    );
  },
);
